'use strict';

define('vb/extensions/dynamic/private/helpers/layoutSerializer',['vb/private/log'], (Log) => {
  const logger = Log.getLogger('/vb/extensions/dynamic/private/helpers/LayoutSerializer');

  /**
   * The LayoutSerializer class is responsible for serializing layout data into a blob.
   * It takes options and resources as input, and returns a serialized blob with a hash.
   */
  class LayoutSerializer {
    /**
     * Creates an instance of LayoutSerializer.
     *
     * @param {object} options - The options to be used for serialization.
     *                           These options will be stringified and stored internally.
     */
    constructor(options) {
      this.options = JSON.stringify(options);
      this.resourceMap = {};
    }

    /**
     * Gets the version of the LayoutSerializer.
     *
     * @static
     * @readonly
     * @type {number}
     */
    static get VERSION() {
      return 1;
    }

    /**
     * Generates a hash code for the given input string.
     *
     * The hash code is generated using a simple hashing algorithm that iterates over the characters in the
     * input string.
     *
     * @static
     * @param {string} inputString - The input string for which to generate a hash code.
     * @returns {number} The generated hash code.
     */
    static generateHashCode(inputString) {
      let hash = 0;
      if (inputString.length === 0) {
        return hash;
      }

      for (let i = 0; i < inputString.length; i++) {
        const charCode = inputString.charCodeAt(i);
        // eslint-disable-next-line no-bitwise
        hash = ((hash << 5) - hash) + charCode;
        // eslint-disable-next-line no-bitwise
        hash &= hash; // Convert to 32bit integer
      }
      return hash;
    }

    /**
     * Adds a text resource to the serializer's resource map.
     *
     * @param {string} path - The path or key for the resource.
     * @param {string} resource - The text resource to be stored.
     */
    addTextResource(path, resource) {
      // Resource loading can complete after the owning metadata descriptor has been disposed. In that case the
      // serializer is no longer usable, so ignore the late resource instead of failing normal layout rendering.
      if (this.resourceMap) {
        this.resourceMap[path] = resource;
      }
    }

    /**
     * Adds a JSON resource to the serializer's resource map.
     * The JSON resource is stringified before being stored.
     *
     * @param {string} path - The path or key for the resource.
     * @param {object} resource - The JSON resource to be stored.
     */
    addJsonResource(path, resource) {
      this.addTextResource(path, JSON.stringify(resource));
    }

    /**
     * Serializes the layout data into a blob with a hash.
     *
     * The serialization process involves creating a data object that contains the version, options, and resource map.
     * This data object is then stringified and a hash code is generated for it.
     * The serialized data is returned as a blob along with its corresponding hash code.
     *
     * @returns {{hash: number, blob: Blob}} An object containing the serialized layout data as a blob and its hash
     * code. The object has two properties: `hash` (number) and `blob` (Blob).
     */
    serialize() {
      const data = {
        version: LayoutSerializer.VERSION,
        options: this.options,
        resourceMap: this.resourceMap,
      };
      const dataStr = JSON.stringify(data);
      const hash = LayoutSerializer.generateHashCode(dataStr);

      logger.info('Serializing layout', data, 'with hash', hash);

      return {
        hash,
        blob: new Blob([dataStr]),
      };
    }

    /**
     * Releases the resources held by the LayoutSerializer instance.
     *
     * This method is used to free up memory by setting the internal state to null.
     * After calling this method, the instance is no longer usable.
     */
    dispose() {
      this.options = null;
      this.resourceMap = null;
    }
  }

  return LayoutSerializer;
});

